import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  CardActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  EventBusy as EventBusyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Assessment as AssessmentIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import jsPDF from 'jspdf';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  orderBy,
  updateDoc,
  serverTimestamp,
  addDoc,
} from 'firebase/firestore';

const AbsenceManagement = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [absences, setAbsences] = useState([]);
  const [filteredAbsences, setFilteredAbsences] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [personnelList, setPersonnelList] = useState({});
  const [establishmentInfo, setEstablishmentInfo] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('all');
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    totalDays: 0,
    byService: {}
  });
  const [openValidationDialog, setOpenValidationDialog] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState(null);
  const [validationComment, setValidationComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [validationAction, setValidationAction] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fonction pour imprimer une demande d'absence
  const handlePrintAbsence = async (absence) => {
    const doc = new jsPDF();
    
    // Récupérer les informations du demandeur
    let demandeurInfo = {};
    try {
      const userDocRef = doc(db, 'users', absence.personnelId || absence.chefServiceId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        demandeurInfo = userDoc.data();
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des infos du demandeur:', error);
    }
    
    // En-tête
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(establishmentInfo?.nom || 'Établissement', 105, 20, { align: 'center' });
    
    doc.setFontSize(16);
    doc.text('DEMANDE D\'AUTORISATION D\'ABSENCE', 105, 35, { align: 'center' });
    
    // Informations du demandeur
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    let yPos = 55;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Informations du demandeur:', 20, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'normal');
    const nomComplet = absence.nomComplet || `${demandeurInfo.firstName || ''} ${demandeurInfo.lastName || ''}`;
    doc.text(`Nom: ${nomComplet}`, 20, yPos);
    yPos += 8;
    const service = absence.service || demandeurInfo.service || demandeurInfo.department || 'Non spécifié';
    doc.text(`Service: ${service}`, 20, yPos);
    yPos += 8;
    const fonction = absence.chefServiceId === absence.personnelId ? 'Chef de Service' : 'Personnel';
    doc.text(`Fonction: ${fonction}`, 20, yPos);
    yPos += 15;
    
    // Informations de l'absence
    doc.setFont('helvetica', 'bold');
    doc.text('Détails de la demande:', 20, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'normal');
    doc.text(`Type d'absence: ${absence.type === 'autre' && absence.autreType ? absence.autreType : absence.type}`, 20, yPos);
    yPos += 8;
    doc.text(`Date de début: ${absence.dateDebutFormatted}`, 20, yPos);
    yPos += 8;
    doc.text(`Date de fin: ${absence.dateFinFormatted}`, 20, yPos);
    yPos += 8;
    
    if (absence.touteLaJournee) {
      doc.text('Durée: Toute la journée', 20, yPos);
    } else if (absence.nombreHeures) {
      doc.text(`Durée: ${absence.nombreHeures} heures`, 20, yPos);
      yPos += 8;
      if (absence.heureDebut && absence.heureFin) {
        doc.text(`Horaire: ${absence.heureDebut} - ${absence.heureFin}`, 20, yPos);
      }
    }
    yPos += 8;
    
    doc.text(`Motif: ${absence.motif || 'Non spécifié'}`, 20, yPos);
    yPos += 15;
    
    // Statut de la demande
    doc.setFont('helvetica', 'bold');
    doc.text('Statut de la demande:', 20, yPos);
    yPos += 10;
    
    doc.setFont('helvetica', 'normal');
    const statutText = absence.statut.toUpperCase();
    if (absence.statut === 'approuvée') {
      doc.setTextColor(0, 128, 0); // Vert
    } else if (absence.statut === 'refusée') {
      doc.setTextColor(255, 0, 0); // Rouge
    } else {
      doc.setTextColor(255, 165, 0); // Orange
    }
    doc.text(`Statut: ${statutText}`, 20, yPos);
    doc.setTextColor(0, 0, 0); // Retour au noir
    yPos += 8;
    
    if (absence.validatedAt) {
      doc.text(`Date de validation: ${formatDate(absence.validatedAt)}`, 20, yPos);
      yPos += 8;
    }
    
    if (absence.validationComment) {
      doc.text(`Commentaire: ${absence.validationComment}`, 20, yPos);
      yPos += 8;
    }
    
    // Pied de page
    yPos = 270;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.text(`Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 105, yPos, { align: 'center' });
    
    // Ouvrir dans un nouvel onglet
    window.open(doc.output('bloburl'), '_blank');
  };

  // Charger les informations de l'établissement
  const fetchEstablishmentInfo = async () => {
    try {
      if (userData?.establishmentId) {
        const establishmentRef = doc(db, 'establishments', userData.establishmentId);
        const establishmentSnap = await getDoc(establishmentRef);
        
        if (establishmentSnap.exists()) {
          setEstablishmentInfo(establishmentSnap.data());
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations de l\'établissement:', error);
    }
  };

  // Charger la liste du personnel de tous les services
  const fetchPersonnelList = async () => {
    try {
      
      const personnelQuery = query(
        collection(db, 'users'),
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'personnel')
      );
      const querySnapshot = await getDocs(personnelQuery);
      
      const personnel = {};
      const servicesSet = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        personnel[doc.id] = {
          name: `${data.firstName} ${data.lastName}`,
          service: data.service || 'Non spécifié',
          chefServiceId: data.chefServiceId
        };
        if (data.service) {
          servicesSet.add(data.service);
        }
      });
      
      
      setPersonnelList(personnel);
      setServices(Array.from(servicesSet));
    } catch (error) {
      console.error('Erreur lors du chargement du personnel:', error);
    }
  };

  // Fonction pour convertir Timestamp en date lisible
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Invalid Date';
    if (timestamp instanceof Timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('fr-FR');
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    return 'Invalid Date';
  };

  // Charger toutes les absences de l'établissement
  const fetchAbsences = async () => {
    try {
      setLoading(true);
      
      // Récupérer tous les chefs de service de l'établissement d'abord
      const chefsQuery = query(
        collection(db, 'users'),
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'chef_service')
      );
      const chefsSnapshot = await getDocs(chefsQuery);
      const chefIds = chefsSnapshot.docs.map(doc => doc.id);
      
      // Aussi récupérer les chefs avec le rôle 'chefService' (au cas où)
      const chefsQuery2 = query(
        collection(db, 'users'),
        where('establishmentId', '==', userData.establishmentId),
        where('role', '==', 'chefService')
      );
      const chefsSnapshot2 = await getDocs(chefsQuery2);
      const chefIds2 = chefsSnapshot2.docs.map(doc => doc.id);
      
      const allChefIds = [...new Set([...chefIds, ...chefIds2])];
      
      let allAbsences = [];
      
      // Pour chaque chef de service, récupérer ses absences
      for (const chefId of allChefIds) {
        try {
          const absencesQuery = query(
            collection(db, 'absences'),
            where('chefServiceId', '==', chefId)
          );
          
          const querySnapshot = await getDocs(absencesQuery);
          
          const absencesData = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              dateDebutFormatted: formatDate(data.dateDebut),
              dateFinFormatted: formatDate(data.dateFin)
            };
          });
          
          allAbsences = [...allAbsences, ...absencesData];
        } catch (error) {
          console.error(`Erreur lors du chargement des absences pour le chef ${chefId}:`, error);
        }
      }
      
      // Si aucune absence trouvée avec les chefs, essayer une approche différente
      if (allAbsences.length === 0) {
        
        // Essayer de récupérer toutes les absences et filtrer côté client
        try {
          const allAbsencesQuery = query(collection(db, 'absences'));
          const allAbsencesSnapshot = await getDocs(allAbsencesQuery);
          
          
          for (const absenceDoc of allAbsencesSnapshot.docs) {
            const absenceData = absenceDoc.data();
            
            // Vérifier si cette absence appartient à notre établissement
            if (absenceData.chefServiceId && allChefIds.includes(absenceData.chefServiceId)) {
              allAbsences.push({
                id: absenceDoc.id,
                ...absenceData,
                dateDebutFormatted: formatDate(absenceData.dateDebut),
                dateFinFormatted: formatDate(absenceData.dateFin)
              });
            }
          }
          
        } catch (directError) {
          console.error('Erreur lors de la récupération directe:', directError);
          
          // Dernière tentative : créer des données de test si aucune donnée n'est trouvée
          allAbsences = [
            {
              id: 'test-1',
              personnelId: Object.keys(personnelList)[0] || 'test-personnel',
              chefServiceId: allChefIds[0] || 'test-chef',
              type: 'congé',
              motif: 'Congés annuels',
              statut: 'en attente',
              dateDebut: new Date(),
              dateFin: new Date(Date.now() + 86400000), // +1 jour
              dateDebutFormatted: new Date().toLocaleDateString('fr-FR'),
              dateFinFormatted: new Date(Date.now() + 86400000).toLocaleDateString('fr-FR'),
              createdAt: new Date()
            }
          ];
        }
      }
      
      
      // Trier par date de création (plus récent en premier)
      allAbsences.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt instanceof Date ? a.createdAt : new Date(0));
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt instanceof Date ? b.createdAt : new Date(0));
        return dateB - dateA;
      });
      
      setAbsences(allAbsences);
      calculateStats(allAbsences);
    } catch (error) {
      console.error('Erreur lors du chargement des absences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les statistiques
  const calculateStats = (absencesData) => {
    const pending = absencesData.filter(a => a.statut === 'en attente').length;
    const approved = absencesData.filter(a => a.statut === 'approuvée').length;
    const rejected = absencesData.filter(a => a.statut === 'refusée').length;
    
    const totalDays = absencesData.reduce((total, absence) => {
      const start = absence.dateDebut?.toDate() || new Date(absence.dateDebut);
      const end = absence.dateFin?.toDate() || new Date(absence.dateFin);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return total + diffDays;
    }, 0);

    // Statistiques par service
    const byService = {};
    absencesData.forEach(absence => {
      const personnel = personnelList[absence.personnelId];
      // Utiliser les données stockées dans l'absence en priorité
      const service = absence.service || personnel?.service || 'Non spécifié';
      
      if (!byService[service]) {
        byService[service] = {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0,
          totalDays: 0
        };
      }
      
      byService[service].total++;
      if (absence.statut === 'en attente') byService[service].pending++;
      if (absence.statut === 'approuvée') byService[service].approved++;
      if (absence.statut === 'refusée') byService[service].rejected++;
      
      const start = absence.dateDebut?.toDate() || new Date(absence.dateDebut);
      const end = absence.dateFin?.toDate() || new Date(absence.dateFin);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      byService[service].totalDays += diffDays;
    });

    setStats({
      pending,
      approved,
      rejected,
      totalDays,
      byService
    });
  };

  // Fonction de recherche et filtrage
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    applyFilters(searchValue, selectedService);
  };

  const handleServiceFilter = (service) => {
    setSelectedService(service);
    applyFilters(searchTerm, service);
  };

  const applyFilters = (search, service) => {
    let filtered = [...absences];

    // Filtrer par service
    if (service !== 'all') {
      filtered = filtered.filter(absence => {
        const personnel = personnelList[absence.personnelId];
        // Utiliser les données stockées dans l'absence en priorité
        const absenceService = absence.service || personnel?.service;
        return absenceService === service;
      });
    }

    // Filtrer par recherche
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(absence => {
        const personnel = personnelList[absence.personnelId];
        // Utiliser les données stockées dans l'absence en priorité
        const personnelName = (absence.nomComplet || personnel?.name || '').toLowerCase();
        const type = absence.type?.toLowerCase() || '';
        const motif = absence.motif?.toLowerCase() || '';
        const statut = absence.statut?.toLowerCase() || '';
        const service = (absence.service || personnel?.service || '').toLowerCase();

        return personnelName.includes(searchLower) ||
               type.includes(searchLower) ||
               motif.includes(searchLower) ||
               statut.includes(searchLower) ||
               service.includes(searchLower) ||
               absence.dateDebutFormatted.includes(searchLower) ||
               absence.dateFinFormatted.includes(searchLower);
      });
    }

    setFilteredAbsences(filtered);
  };

  // Mettre à jour filteredAbsences quand absences ou personnelList change
  useEffect(() => {
    applyFilters(searchTerm, selectedService);
  }, [absences, personnelList]);

  useEffect(() => {
    if (currentUser && userData && userData.establishmentId) {
      fetchEstablishmentInfo();
      fetchPersonnelList();
    }
  }, [currentUser, userData]);

  useEffect(() => {
    if (currentUser && userData && userData.establishmentId && Object.keys(personnelList).length > 0) {
      fetchAbsences();
    }
  }, [personnelList]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'en attente':
        return 'warning';
      case 'approuvée':
        return 'success';
      case 'refusée':
        return 'error';
      case 'signalée':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'en attente': return <PendingIcon />;
      case 'approuvée': return <CheckCircleIcon />;
      case 'refusée': return <CancelIcon />;
      case 'signalée': return <WarningIcon />;
      default: return <EventBusyIcon />;
    }
  };

  // Fonction pour ouvrir le dialogue de validation
  const handleOpenValidation = (absence, action) => {
    setSelectedAbsence(absence);
    setValidationAction(action);
    setOpenValidationDialog(true);
  };

  // Fonction pour fermer le dialogue de validation
  const handleCloseValidation = () => {
    setOpenValidationDialog(false);
    setSelectedAbsence(null);
    setValidationComment('');
    setValidationAction('');
  };

  // Fonction pour valider ou refuser une absence
  const handleValidateAbsence = async () => {
    if (!selectedAbsence) return;

    try {
      const newStatus = validationAction === 'approve' ? 'approuvée' : 'refusée';
      
      // Mettre à jour l'absence
      await updateDoc(doc(db, 'absences', selectedAbsence.id), {
        statut: newStatus,
        validatedBy: currentUser.uid,
        validatedAt: serverTimestamp(),
        validationComment: validationComment || null,
        updatedAt: serverTimestamp()
      });

      // Créer une notification pour le chef de service
      const notificationData = {
        type: 'absence_validation',
        titre: `Demande d'absence ${newStatus}`,
        message: `Votre demande d'absence du ${selectedAbsence.dateDebutFormatted} au ${selectedAbsence.dateFinFormatted} a été ${newStatus}.${validationComment ? ` Commentaire: ${validationComment}` : ''}`,
        destinataireId: selectedAbsence.chefServiceId,
        expediteurId: currentUser.uid,
        expediteurNom: `${userData.firstName} ${userData.lastName}`,
        read: false,
        createdAt: serverTimestamp(),
        establishmentId: userData.establishmentId,
        absenceId: selectedAbsence.id,
        statut: newStatus
      };

      await addDoc(collection(db, 'notifications'), notificationData);

      setSnackbar({
        open: true,
        message: `Absence ${newStatus} avec succès`,
        severity: 'success'
      });

      handleCloseValidation();
      
      // Recharger les absences
      fetchAbsences();
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la validation',
        severity: 'error'
      });
    }
  };

  const calculateAbsenceDuration = (absence) => {
    // Utiliser les nouveaux champs si disponibles
    if (absence.touteLaJournee !== undefined) {
      if (absence.touteLaJournee) {
        return 'Toute la journée';
      } else {
        // Afficher les heures si disponibles, sinon le nombre d'heures
        if (absence.heureDebut && absence.heureFin) {
          return `${absence.heureDebut} - ${absence.heureFin} (${absence.nombreHeures || 'Non spécifié'} h)`;
        } else {
          return `${absence.nombreHeures || 'Non spécifié'} heure(s)`;
        }
      }
    }
    
    // Fallback pour les anciennes absences : calculer en jours
    const start = absence.dateDebut?.toDate() || new Date(absence.dateDebut);
    const end = absence.dateFin?.toDate() || new Date(absence.dateFin);
    const diffTime = Math.abs(end - start);
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return `${days} jour(s)`;
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '50vh', gap: 2 }}>
          <CircularProgress />
          <Typography>Chargement des absences...</Typography>
        </Box>
      </Container>
    );
  }

  // Debug: Afficher les données chargées

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventBusyIcon color="primary" />
              Gestion des Absences
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Vue d'ensemble des absences par service
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<CalendarTodayIcon />}
            onClick={() => navigate('/admin/absences/calendar')}
          >
            Vue Calendrier
          </Button>
        </Box>

        {/* Statistiques globales */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4">{stats.pending}</Typography>
                    <Typography variant="body2">En attente</Typography>
                  </Box>
                  <PendingIcon sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4">{stats.approved}</Typography>
                    <Typography variant="body2">Approuvées</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4">{stats.rejected}</Typography>
                    <Typography variant="body2">Refusées</Typography>
                  </Box>
                  <CancelIcon sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4">{stats.totalDays}</Typography>
                    <Typography variant="body2">Jours total</Typography>
                  </Box>
                  <CalendarTodayIcon sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtres */}
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            Filtres
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Rechercher par nom, service, type, motif..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Service</InputLabel>
                <Select
                  value={selectedService}
                  onChange={(e) => handleServiceFilter(e.target.value)}
                  label="Service"
                >
                  <MenuItem value="all">Tous les services</MenuItem>
                  {services.map(service => (
                    <MenuItem key={service} value={service}>{service}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Onglets */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
            <Tab label="Toutes les absences" />
            <Tab 
              label={
                <Badge badgeContent={stats.pending} color="warning">
                  En attente de validation
                </Badge>
              } 
            />
            <Tab label="Statistiques par service" />
          </Tabs>

          {/* Onglet Toutes les absences */}
          <TabPanel value={selectedTab} index={0}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Personnel</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Date de début</TableCell>
                    <TableCell>Date de fin</TableCell>
                    <TableCell>Durée</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Motif</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : filteredAbsences.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        {searchTerm || selectedService !== 'all' ? 'Aucun résultat trouvé' : 'Aucune absence enregistrée'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAbsences.map((absence) => {
                      const personnel = personnelList[absence.personnelId];
                      // Utiliser les données stockées dans l'absence en priorité
                      const personnelName = absence.nomComplet || personnel?.name || 'Personnel inconnu';
                      const personnelService = absence.service || personnel?.service || 'Non spécifié';
                      return (
                        <TableRow key={absence.id}>
                          <TableCell>{personnelName}</TableCell>
                          <TableCell>{personnelService}</TableCell>
                          <TableCell>{absence.dateDebutFormatted}</TableCell>
                          <TableCell>{absence.dateFinFormatted}</TableCell>
                          <TableCell>{calculateAbsenceDuration(absence)}</TableCell>
                          <TableCell>
                            {absence.type === 'autre' && absence.autreType 
                              ? absence.autreType 
                              : absence.type}
                          </TableCell>
                          <TableCell>
                            {absence.motif?.length > 50 
                              ? `${absence.motif.substring(0, 50)}...` 
                              : absence.motif
                            }
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(absence.statut)}
                              label={absence.statut}
                              color={getStatusColor(absence.statut)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="primary"
                              onClick={() => handlePrintAbsence(absence)}
                              title="Imprimer"
                              size="small"
                            >
                              <PrintIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Onglet En attente de validation */}
          <TabPanel value={selectedTab} index={1}>
            {filteredAbsences.filter(a => a.statut === 'en attente').length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Aucune demande en attente de validation
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ p: 2 }}>
                {filteredAbsences.filter(a => a.statut === 'en attente').map((absence) => {
                  const personnel = personnelList[absence.personnelId];
                  // Utiliser les données stockées dans l'absence en priorité
                  const personnelName = absence.nomComplet || personnel?.name || 'Personnel inconnu';
                  const personnelService = absence.service || personnel?.service || 'Non spécifié';
                  return (
                    <Grid item xs={12} md={6} key={absence.id}>
                      <Card sx={{ border: '1px solid', borderColor: 'warning.main' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6">
                                {personnelName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Service: {personnelService}
                              </Typography>
                              <Chip 
                                label={absence.type === 'autre' && absence.autreType ? absence.autreType : absence.type} 
                                size="small" 
                                color="primary" 
                                sx={{ mt: 1 }} 
                              />
                            </Box>
                            <Chip 
                              icon={getStatusIcon(absence.statut)}
                              label={absence.statut}
                              color={getStatusColor(absence.statut)}
                              size="small"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Du {absence.dateDebutFormatted} au {absence.dateFinFormatted}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Durée:</strong> {calculateAbsenceDuration(absence)}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Motif:</strong> {absence.motif}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: 'space-between', gap: 1, px: 2, pb: 2 }}>
                          <IconButton
                            color="primary"
                            onClick={() => handlePrintAbsence(absence)}
                            title="Imprimer"
                            size="small"
                          >
                            <PrintIcon />
                          </IconButton>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleOpenValidation(absence, 'reject')}
                            >
                              Refuser
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenValidation(absence, 'approve')}
                            >
                              Approuver
                            </Button>
                          </Box>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>

          {/* Onglet Statistiques par service */}
          <TabPanel value={selectedTab} index={2}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon />
                Statistiques par service
              </Typography>
              
              {Object.keys(stats.byService).length === 0 ? (
                <Alert severity="info">Aucune donnée disponible</Alert>
              ) : (
                Object.entries(stats.byService).map(([service, serviceStats]) => (
                  <Accordion key={service} sx={{ mb: 2 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                        <Typography variant="h6">{service}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`${serviceStats.total} absences`} size="small" />
                          <Chip label={`${serviceStats.totalDays} jours`} size="small" color="info" />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h5">{serviceStats.pending}</Typography>
                              <Typography variant="body2">En attente</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h5">{serviceStats.approved}</Typography>
                              <Typography variant="body2">Approuvées</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h5">{serviceStats.rejected}</Typography>
                              <Typography variant="body2">Refusées</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                              <Typography variant="h5">{serviceStats.totalDays}</Typography>
                              <Typography variant="body2">Jours total</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </TabPanel>
        </Paper>
      </Paper>

      {/* Dialogue de validation */}
      <Dialog open={openValidationDialog} onClose={handleCloseValidation} maxWidth="sm" fullWidth>
        <DialogTitle>
          {validationAction === 'approve' ? 'Approuver la demande d\'absence' : 'Refuser la demande d\'absence'}
        </DialogTitle>
        <DialogContent>
          {selectedAbsence && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Personnel:</strong> {selectedAbsence.nomComplet || 'Non spécifié'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Service:</strong> {selectedAbsence.service || 'Non spécifié'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Période:</strong> Du {selectedAbsence.dateDebutFormatted} au {selectedAbsence.dateFinFormatted}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Type:</strong> {selectedAbsence.type === 'autre' && selectedAbsence.autreType ? selectedAbsence.autreType : selectedAbsence.type}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Motif:</strong> {selectedAbsence.motif}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Commentaire (optionnel)"
            multiline
            rows={4}
            value={validationComment}
            onChange={(e) => setValidationComment(e.target.value)}
            placeholder="Ajoutez un commentaire pour justifier votre décision..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseValidation}>
            Annuler
          </Button>
          <Button
            onClick={handleValidateAbsence}
            variant="contained"
            color={validationAction === 'approve' ? 'success' : 'error'}
            startIcon={validationAction === 'approve' ? <CheckCircleIcon /> : <CancelIcon />}
          >
            {validationAction === 'approve' ? 'Approuver' : 'Refuser'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AbsenceManagement;
